import React, { useEffect, useMemo, useState } from 'react';
import { X, Palette, Loader2, Search, ArrowLeft, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import BaskiGorselUpload from '../production/BaskiGorselUpload';
import SearchableSelect from '../common/SearchableSelect';
import { customerPortalService } from '../../services/customerPortalApi';

const CustomerPatternVariantModal = ({
  isOpen,
  onClose,
  onSuccess,
  pattern = null,
  stockCards = []
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showMediantegSearch, setShowMediantegSearch] = useState(false);
  const [mediantegSearchTerm, setMediantegSearchTerm] = useState('');
  const [mediantegSearchResults, setMediantegSearchResults] = useState([]);
  const [mediantegAllOrders, setMediantegAllOrders] = useState([]);
  const [searchingMedianteg, setSearchingMedianteg] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [selectedMediantegImageUrls, setSelectedMediantegImageUrls] = useState(new Set());
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [formData, setFormData] = useState({
    notlar: '',
    stok_kart_id: '',
    stok_kart_adi: ''
  });

  useEffect(() => {
    if (!isOpen || !pattern) return;

    const selectedStockCard =
      stockCards.find((item) => String(item.id) === String(pattern.stockCardId)) || null;

    setFormData({
      notlar: '',
      stok_kart_id: pattern.stockCardId ? String(pattern.stockCardId) : '',
      stok_kart_adi: selectedStockCard?.name || ''
    });
    setUploadedImages([]);
    setShowMediantegSearch(false);
    setMediantegSearchTerm('');
    setMediantegSearchResults([]);
    setMediantegAllOrders([]);
    setSelectedOrderDetails(null);
    setSelectedMediantegImageUrls(new Set());
  }, [isOpen, pattern, stockCards]);

  const selectedStockCardOption = useMemo(() => {
    if (!formData.stok_kart_id) return '';
    return String(formData.stok_kart_id);
  }, [formData.stok_kart_id]);

  useEffect(() => {
    if (!showMediantegSearch) {
      return;
    }

    const term = mediantegSearchTerm.trim().toLowerCase();
    if (!term) {
      setMediantegSearchResults(mediantegAllOrders);
      return;
    }

    const filtered = mediantegAllOrders.filter((order) => {
      const orderNo = String(order.orderNo || '').toLowerCase();
      const modelNo = String(order.modelNo || '').toLowerCase();
      return orderNo.includes(term) || modelNo.includes(term);
    });

    setMediantegSearchResults(filtered);
  }, [showMediantegSearch, mediantegSearchTerm, mediantegAllOrders]);

  const handleStokKartChange = (e) => {
    const nextValue = e.target.value;
    const selected = stockCards.find((item) => String(item.id) === String(nextValue));
    setFormData((prev) => ({
      ...prev,
      stok_kart_id: nextValue,
      stok_kart_adi: selected?.name || ''
    }));
  };

  const mergeImages = (newImages) => {
    setUploadedImages((prev) => {
      const existingKeys = new Set(
        prev.map((image) => image.originalUrl || image.preview || image.id)
      );
      const merged = [...prev];

      newImages.forEach((image) => {
        const imageKey = image.originalUrl || image.preview || image.id;
        if (!existingKeys.has(imageKey)) {
          existingKeys.add(imageKey);
          merged.push(image);
        }
      });

      return merged;
    });
  };

  const loadMediantegOrdersOnOpen = async () => {
    setShowMediantegSearch(true);
    setSearchingMedianteg(true);

    try {
      const response = await customerPortalService.get('/orders/medianteg/active');
      if (response.data?.success) {
        const orders = response.data.data || [];
        setMediantegAllOrders(orders);
        setMediantegSearchResults(orders);
      } else {
        setMediantegAllOrders([]);
        setMediantegSearchResults([]);
        toast.error(response.data?.message || 'Medianteg siparişleri yüklenemedi');
      }
    } catch (error) {
      console.error('Medianteg aktif siparişleri yüklenemedi:', error);
      setMediantegAllOrders([]);
      setMediantegSearchResults([]);
      toast.error('Medianteg siparişleri yüklenemedi');
    } finally {
      setSearchingMedianteg(false);
    }
  };

  const handleSelectMediantegOrder = async (orderId) => {
    setLoadingOrderDetails(true);
    try {
      const response = await customerPortalService.get(`/orders/medianteg/order/${orderId}`, {
        params: { allowWithoutStockCard: 1 }
      });
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Sipariş detayları yüklenemedi');
        return;
      }

      const orderDetails = response.data.data || {};
      const allUrls = Array.isArray(orderDetails.allUrls) && orderDetails.allUrls.length > 0
        ? orderDetails.allUrls
        : orderDetails.imageUrl
        ? [orderDetails.imageUrl]
        : [];

      if (allUrls.length === 0) {
        toast.error('Bu siparişte aktarılacak görsel bulunamadı');
        return;
      }

      setSelectedOrderDetails({ ...orderDetails, orderId, allUrls });
      setSelectedMediantegImageUrls(new Set());
    } catch (error) {
      console.error('Medianteg sipariş detay hatası:', error);
      toast.error(error.response?.data?.message || 'Sipariş detayları yüklenemedi');
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleToggleMediantegImage = (url) => {
    setSelectedMediantegImageUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const handleApplySelectedMediantegImages = () => {
    if (!selectedOrderDetails) return;

    const urlsToAdd = Array.from(selectedMediantegImageUrls);
    if (urlsToAdd.length === 0) {
      toast.error('En az 1 görsel seçin');
      return;
    }

    const remoteImages = urlsToAdd.map((imageUrl, index) => ({
      id: `medianteg_order_${selectedOrderDetails.orderId}_${index}_${Date.now()}`,
      file: null,
      preview: imageUrl,
      originalUrl: imageUrl,
      name: `Janes ${selectedOrderDetails.orderNo || selectedOrderDetails.orderId} ${index + 1}`,
      size: 0,
      status: 'success',
      isUrl: true
    }));

    mergeImages(remoteImages);
    toast.success('Seçilen görseller forma yüklendi');

    setSelectedOrderDetails(null);
    setSelectedMediantegImageUrls(new Set());
    setShowMediantegSearch(false);
    setMediantegSearchTerm('');
  };

  const handleBackToOrderList = () => {
    setSelectedOrderDetails(null);
    setSelectedMediantegImageUrls(new Set());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pattern?.id) {
      toast.error('Desen bilgisi bulunamadı');
      return;
    }

    if (uploadedImages.length === 0) {
      toast.error('En az 1 görsel yüklemelisiniz');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('notlar', formData.notlar || '');
      submitData.append('stok_kart_id', formData.stok_kart_id || '');

      const currentGorselUrls = [];
      uploadedImages.forEach((image) => {
        if (image?.file instanceof File) {
          submitData.append('gorseller', image.file);
          return;
        }

        const remoteUrl = image?.originalUrl || image?.preview;
        if (remoteUrl && !currentGorselUrls.includes(remoteUrl)) {
          currentGorselUrls.push(remoteUrl);
        }
      });

      if (currentGorselUrls.length > 0) {
        submitData.append('currentGorselUrls', JSON.stringify(currentGorselUrls));
      }

      const response = await customerPortalService.post(
        `/digital-printing/patterns/${pattern.id}/variants`,
        submitData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data?.success) {
        toast.success('Varyant oluşturuldu');
        onSuccess(response.data.data);
        onClose();
        return;
      }

      toast.error(response.data?.message || 'Varyant oluşturulamadı');
    } catch (error) {
      console.error('Customer varyant oluşturma hatası:', error);
      toast.error(error.response?.data?.message || 'Varyant oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !pattern) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-2 px-2 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full">
            <form onSubmit={handleSubmit}>
              <div className="bg-white dark:bg-gray-800 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center">
                    <Palette className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400 mr-2 sm:mr-3" />
                    <div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                        Varyant Ekle
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Ana desen: {pattern.desenNo}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={loadMediantegOrdersOnOpen}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium"
                    >
                      <Search className="w-3.5 h-3.5" />
                      <span>Janes Siparişlerinden Seç</span>
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Görseller
                  </label>
                  <BaskiGorselUpload
                    images={uploadedImages}
                    onImagesChange={setUploadedImages}
                    maxSize={5 * 1024 * 1024}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Dosya yükleyebilir veya Janes siparişinden gelen görselleri ekleyebilirsiniz.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stok Kartı (Opsiyonel)
                  </label>
                  <SearchableSelect
                    options={stockCards}
                    value={selectedStockCardOption}
                    onChange={handleStokKartChange}
                    placeholder="Stok kartı seç (opsiyonel)"
                    searchPlaceholder="Stok kartı ara..."
                    getOptionLabel={(item) => item.name || item.stockCode || `Stok #${item.id}`}
                    getOptionValue={(item) => item.id}
                    name="variantStockCard"
                    isClearable
                  />
                </div>

                <div>
                  <label htmlFor="variant-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notlar (Opsiyonel)
                  </label>
                  <textarea
                    id="variant-notes"
                    name="notlar"
                    value={formData.notlar}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notlar: e.target.value
                      }))
                    }
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Ek notlar..."
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 sm:px-6 py-3 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Oluşturuluyor...
                    </div>
                  ) : (
                    'Oluştur'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showMediantegSearch && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <div className="flex items-center gap-2">
                {selectedOrderDetails && (
                  <button
                    type="button"
                    onClick={handleBackToOrderList}
                    className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedOrderDetails
                    ? `Görsel Seç - ${selectedOrderDetails.orderNo || selectedOrderDetails.orderId}`
                    : 'Medianteg Sipariş Ara'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowMediantegSearch(false);
                  setMediantegSearchTerm('');
                  setSelectedOrderDetails(null);
                  setSelectedMediantegImageUrls(new Set());
                }}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {selectedOrderDetails ? (
              <>
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    İstediğiniz görsellere tıklayarak seçin. Seçilenleri eklemek için alttaki butona basın.
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {selectedOrderDetails.allUrls.map((url) => {
                      const isSelected = selectedMediantegImageUrls.has(url);
                      return (
                        <button
                          key={url}
                          type="button"
                          onClick={() => handleToggleMediantegImage(url)}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                            isSelected
                              ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <img
                            src={url}
                            alt="Görsel"
                            className={`w-full h-full object-cover ${!isSelected ? 'opacity-80' : ''}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                              <Check className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedMediantegImageUrls.size} görsel seçildi
                  </span>
                  <button
                    type="button"
                    onClick={handleApplySelectedMediantegImages}
                    disabled={selectedMediantegImageUrls.size === 0}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium"
                  >
                    Seçilenleri Ekle
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={mediantegSearchTerm}
                      onChange={(e) => setMediantegSearchTerm(e.target.value)}
                      placeholder="Sipariş no veya model no girin..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  {searchingMedianteg ? (
                    <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Siparişler yükleniyor...
                    </div>
                  ) : loadingOrderDetails ? (
                    <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sipariş detayları yükleniyor...
                    </div>
                  ) : mediantegSearchResults.length === 0 ? (
                    <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                      Aktif sipariş bulunamadı
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {mediantegSearchResults.map((order) => (
                        <button
                          key={order.orderId}
                          type="button"
                          onClick={() => handleSelectMediantegOrder(order.orderId)}
                          className="w-full text-left border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex gap-3"
                        >
                          {order.imageUrl && (
                            <img
                              src={order.imageUrl}
                              alt="Sipariş"
                              className="w-14 h-14 object-cover rounded border border-gray-200 dark:border-gray-700"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {order.orderNo}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Model: {order.modelNo || '-'}
                            </p>
                            {order.fabric && (
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Kumaş: {order.fabric}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerPatternVariantModal;
